import React from "react";
import styled from "styled-components";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { DropdownContent } from "@sonder/ui/source/styled/components/DropdownContent";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import {
  CircleButton,
  CircleButtonProps,
} from "@sonder/ui/source/styled/fragments/CircleButton";
import { useSegment } from "analytics/segment";
import {
  AdditionalOption,
  AdditionalOptionInterface,
  convertAdditionalOptionsArrayToObject,
} from "tasks/hooks/useGetAdditionalOptions";
import { TaskStatus } from "typings/globalGQLTypes";

const AdditionalOptionsMenuDropdown = styled(Dropdown)`
  flex-grow: 0;
`;

const AdditionalOptionsMenuDropdownContent = styled(DropdownContent)`
  flex-direction: column;
  left: 0.5rem;
  min-width: 18rem;
`;

const AdditionalOptionsMenuDropdownOption = styled(Button)`
  align-items: flex-start;
  flex-direction: column;
  height: auto;
  max-width: 18rem;
  min-width: 0;
  padding-bottom: 0.7rem;
  padding-top: 0.7rem;
  text-align: left;
  white-space: wrap;
`;

const AdditionalOptionsMenuDropdownLabel = styled.div`
  font-size: 1rem;
  font-weight: bold;
`;

const AdditionalOptionsMenuDropdownDescription = styled.div`
  margin-top: 0.25rem;
`;
interface AdditionalOptionsMenuProps {
  additionalOptions: AdditionalOptionInterface[];
  className?: string;
  eventName: string;
  loading?: boolean;
  onClose?(): void;
  onOpen?(): void;
  status: string | null;
  renderTrigger: () => JSX.Element;
}

interface AdditionalOptionsMenuTriggerProps extends CircleButtonProps {}

export const AdditionalOptionsMenuTrigger = ({
  backgroundVariant,
  ...rest
}: AdditionalOptionsMenuTriggerProps) => (
  <CircleButton
    backgroundVariant={backgroundVariant}
    data-testid="additional-options-button"
    {...rest}
  >
    <MoreHorizIcon />
  </CircleButton>
);

export const AdditionalOptionsMenu = ({
  additionalOptions,
  className,
  eventName,
  loading,
  onClose = () => undefined,
  onOpen = () => undefined,
  renderTrigger,
  status,
}: AdditionalOptionsMenuProps) => {
  const { track } = useSegment();
  const {
    [AdditionalOption.Archive]: archiveOption,
    [AdditionalOption.Unschedule]: unscheduleOption,
  } = convertAdditionalOptionsArrayToObject(additionalOptions);
  return (
    <AdditionalOptionsMenuDropdown
      className={className}
      trigger={renderTrigger && renderTrigger()}
      onClose={onClose}
      onOpen={onOpen}
    >
      {({ isOpen, closePortal }) =>
        isOpen && (
          <AdditionalOptionsMenuDropdownContent align="right">
            {unscheduleOption && (
              <AdditionalOptionsMenuDropdownOption
                noBorder
                disabled={loading || unscheduleOption.disabled}
                onClick={() => {
                  if (unscheduleOption.action) unscheduleOption.action();
                  closePortal();
                  track({
                    eventName,
                    action: "click",
                    element: "unschedule_task_button",
                  });
                }}
              >
                <AdditionalOptionsMenuDropdownLabel>
                  {unscheduleOption.label}
                </AdditionalOptionsMenuDropdownLabel>
                {unscheduleOption.description && (
                  <AdditionalOptionsMenuDropdownDescription>
                    {unscheduleOption.description}
                  </AdditionalOptionsMenuDropdownDescription>
                )}
              </AdditionalOptionsMenuDropdownOption>
            )}
            {archiveOption && (
              <AdditionalOptionsMenuDropdownOption
                noBorder
                disabled={loading || archiveOption.disabled}
                onClick={() => {
                  const nextStatus =
                    status === TaskStatus.canceled
                      ? TaskStatus.ready
                      : TaskStatus.canceled;
                  if (archiveOption.action) archiveOption.action();
                  closePortal();
                  track({
                    eventName,
                    action: "click",
                    element: "cancel_task_button",
                    props: { nextValue: nextStatus },
                  });
                }}
              >
                <AdditionalOptionsMenuDropdownLabel>
                  {archiveOption.label}
                </AdditionalOptionsMenuDropdownLabel>
                {archiveOption.description && (
                  <AdditionalOptionsMenuDropdownDescription>
                    {archiveOption.description}
                  </AdditionalOptionsMenuDropdownDescription>
                )}
              </AdditionalOptionsMenuDropdownOption>
            )}
          </AdditionalOptionsMenuDropdownContent>
        )
      }
    </AdditionalOptionsMenuDropdown>
  );
};
